import "./styles.css";
import {
  formatPrice,
  normalizeSiteConfig,
  safeDestination,
  type SiteConfigInput,
} from "./config";

declare global {
  interface Window {
    NORDTALK_SITE_CONFIG?: SiteConfigInput;
  }
}

const config = normalizeSiteConfig(window.NORDTALK_SITE_CONFIG);
const formattedPrice = formatPrice(config.priceRub);

const setText = (selector: string, value: string) => {
  document.querySelectorAll<HTMLElement>(selector).forEach((element) => {
    element.textContent = value;
  });
};

setText("[data-config='price']", formattedPrice);
setText("[data-config='version']", config.version);
setText("[data-config='installer-size']", config.installerSize);
setText("[data-config='year']", String(new Date().getFullYear()));

const downloadDestination = safeDestination(
  config.downloadUrl,
  window.location.origin,
);
document
  .querySelectorAll<HTMLAnchorElement>("[data-action='download']")
  .forEach((link) => {
    if (downloadDestination) {
      link.href = downloadDestination;
    } else {
      link.removeAttribute("href");
      link.setAttribute("aria-disabled", "true");
    }
  });

const purchaseDialog = document.querySelector<HTMLDialogElement>(
  "#purchase-placeholder",
);
const purchaseDestination = safeDestination(
  config.purchaseUrl,
  window.location.origin,
);

document
  .querySelectorAll<HTMLAnchorElement>("[data-action='purchase']")
  .forEach((button) => {
    button.addEventListener("click", (event) => {
      event.preventDefault();
      const integrationEvent = new CustomEvent("nordtalk:purchase", {
        bubbles: true,
        cancelable: true,
        detail: {
          priceRub: config.priceRub,
          source: button.dataset.purchaseSource ?? "unknown",
        },
      });
      const shouldContinue = button.dispatchEvent(integrationEvent);
      if (!shouldContinue) return;
      if (purchaseDestination) {
        window.location.assign(purchaseDestination);
        return;
      }
      purchaseDialog?.showModal();
    });
  });

purchaseDialog
  ?.querySelector<HTMLButtonElement>("[data-dialog-close]")
  ?.addEventListener("click", () => purchaseDialog.close());
purchaseDialog?.addEventListener("click", (event) => {
  if (event.target === purchaseDialog) purchaseDialog.close();
});

const menuButton = document.querySelector<HTMLButtonElement>("#menu-toggle");
const navigation = document.querySelector<HTMLElement>("#site-navigation");

const closeMenu = () => {
  menuButton?.setAttribute("aria-expanded", "false");
  menuButton?.setAttribute("aria-label", "Открыть меню");
  navigation?.removeAttribute("data-open");
};

menuButton?.addEventListener("click", () => {
  const opening = menuButton.getAttribute("aria-expanded") !== "true";
  menuButton.setAttribute("aria-expanded", String(opening));
  menuButton.setAttribute(
    "aria-label",
    opening ? "Закрыть меню" : "Открыть меню",
  );
  navigation?.toggleAttribute("data-open", opening);
});

navigation?.querySelectorAll("a").forEach((link) => {
  link.addEventListener("click", closeMenu);
});

document.addEventListener("keydown", (event) => {
  if (event.key === "Escape") {
    closeMenu();
    if (purchaseDialog?.open) purchaseDialog.close();
  }
});

const reducedMotion = window.matchMedia(
  "(prefers-reduced-motion: reduce)",
).matches;
const revealElements = document.querySelectorAll<HTMLElement>("[data-reveal]");

if (reducedMotion || !("IntersectionObserver" in window)) {
  revealElements.forEach((element) => element.setAttribute("data-visible", ""));
} else {
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        (entry.target as HTMLElement).setAttribute("data-visible", "");
        observer.unobserve(entry.target);
      });
    },
    { rootMargin: "0px 0px -8%", threshold: 0.08 },
  );
  revealElements.forEach((element) => observer.observe(element));
}
